/**
 * NMS Super Stars Action Switcher Component
 * Handles hide/show transitions between product blocks with fade animations
 * Follows NMS component instance architecture for performance and maintainability
 */

class NMSSuperStarsActionSwitcher extends window.NMSBaseClass {
  #currentIndex = 0;
  #totalBlocks = 0;
  #isTransitioning = false;

  constructor(element) {
    super();

    // REQUIRED: Store element reference for scoped operations
    this.element = element;
    this.sectionId = element.dataset.sectionId || element.id || `section-${Date.now()}`;
    this.componentId = `super-stars-action-${this.sectionId}`;

    // Get total blocks from data attribute
    this.#totalBlocks = parseInt(element.dataset.totalBlocks || '0', 10);

    // REQUIRED: Register instance in global namespace for external access
    window.neemans = window.neemans || {};
    window.neemans.superStarsAction = window.neemans.superStarsAction || {};
    window.neemans.superStarsAction[this.sectionId] = this;
  }

  init() {
    // REQUIRED: Override base init to scope operations to this.element
    this.#setupComponentRefs();
    this.#bindComponentEvents();
    this.#initializeState();

    const activeButtons = this.#getActiveBlockButtons();
    console.log('[NMS Super Stars Action] Initialized:', {
      sectionId: this.sectionId,
      totalBlocks: this.#totalBlocks,
      hasBlocks: this.refs.blocks?.length > 0,
      hasPrevButton: !!activeButtons.prevButton,
      hasNextButton: !!activeButtons.nextButton,
    });
  }

  destroy() {
    super.destroy();
    // REQUIRED: Clean up global reference
    if (window.neemans.superStarsAction?.[this.sectionId]) {
      delete window.neemans.superStarsAction[this.sectionId];
    }
  }

  // REQUIRED: Component-scoped ref setup
  #setupComponentRefs() {
    this.refs = this.refs || {};

    // Find all blocks
    this.refs.blocks = Array.from(this.element.querySelectorAll('.nms-lp-super-stars-action__block'));
  }

  // Get navigation buttons from currently active block
  #getActiveBlockButtons() {
    const activeBlock = this.element.querySelector('.nms-lp-super-stars-action__block[data-active="true"]');
    if (!activeBlock) return { prevButtonWrapper: null, nextButtonWrapper: null, prevButton: null, nextButton: null };

    const prevButtonWrapper = activeBlock.querySelector('[data-action="prev"]');
    const nextButtonWrapper = activeBlock.querySelector('[data-action="next"]');

    return {
      prevButtonWrapper,
      nextButtonWrapper,
      prevButton: prevButtonWrapper?.querySelector('button'),
      nextButton: nextButtonWrapper?.querySelector('button'),
    };
  }

  // REQUIRED: Component-scoped event binding
  #bindComponentEvents() {
    // Use event delegation to handle clicks on navigation buttons in any block
    this.element.addEventListener('click', (e) => {
      // Find the closest nav button wrapper
      const navButton = e.target.closest('[data-action]');
      if (!navButton) return;

      const action = navButton.getAttribute('data-action');
      e.preventDefault();

      if (action === 'prev') {
        this.#handlePrev();
      } else if (action === 'next') {
        this.#handleNext();
      }
    });

    // Optional: Keyboard navigation
    this.element.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#handleNext();
      }
    });
  }

  #initializeState() {
    // Ensure first block is active
    this.#currentIndex = 0;
    this.#updateBlockStates();
    this.#updateButtonStates();
  }

  // Handle previous button click
  #handlePrev() {
    console.log('[NMS Super Stars Action] Prev clicked:', {
      currentIndex: this.#currentIndex,
      isTransitioning: this.#isTransitioning,
      canGoPrev: this.#currentIndex > 0,
    });

    if (this.#isTransitioning || this.#currentIndex === 0) return;

    this.#currentIndex--;
    this.#switchBlock();
  }

  // Handle next button click
  #handleNext() {
    console.log('[NMS Super Stars Action] Next clicked:', {
      currentIndex: this.#currentIndex,
      isTransitioning: this.#isTransitioning,
      canGoNext: this.#currentIndex < this.#totalBlocks - 1,
    });

    if (this.#isTransitioning || this.#currentIndex >= this.#totalBlocks - 1) return;

    this.#currentIndex++;
    this.#switchBlock();
  }

  // Switch to a block with animation
  #switchBlock() {
    if (this.#isTransitioning) return;

    this.#isTransitioning = true;

    const previousIndex = this.#findCurrentActiveIndex();
    console.log('[NMS Super Stars Action] Switching block:', {
      from: previousIndex,
      to: this.#currentIndex,
    });

    // Dispatch event before transition
    this.dispatchNMSEvent('block-changing', {
      previousIndex,
      currentIndex: this.#currentIndex,
      sectionId: this.sectionId,
    });

    // Update block states (CSS will handle animation)
    this.#updateBlockStates();
    this.#updateButtonStates();

    // Wait for transition to complete
    setTimeout(() => {
      this.#isTransitioning = false;

      console.log('[NMS Super Stars Action] Block switch complete:', this.#currentIndex);

      // Dispatch event after transition
      this.dispatchNMSEvent('block-changed', {
        currentIndex: this.#currentIndex,
        sectionId: this.sectionId,
      });
    }, 500); // Match CSS transition duration
  }

  // Update block visibility states
  #updateBlockStates() {
    if (!this.refs.blocks) return;

    this.refs.blocks.forEach((block, index) => {
      const isActive = index === this.#currentIndex;
      block.setAttribute('data-active', isActive);

      // Update ARIA attributes
      block.setAttribute('aria-hidden', !isActive);
    });
  }

  // Update button disabled states
  #updateButtonStates() {
    const isPrevDisabled = this.#currentIndex === 0;
    const isNextDisabled = this.#currentIndex >= this.#totalBlocks - 1;

    // Get buttons from currently active block
    const { prevButton, nextButton, prevButtonWrapper, nextButtonWrapper } = this.#getActiveBlockButtons();

    // Update prev button
    if (prevButton) {
      prevButton.disabled = isPrevDisabled;
    }
    if (prevButtonWrapper) {
      prevButtonWrapper.setAttribute('aria-disabled', isPrevDisabled);
      if (isPrevDisabled) {
        prevButtonWrapper.style.pointerEvents = 'none';
      } else {
        prevButtonWrapper.style.pointerEvents = '';
      }
    }

    // Update next button
    if (nextButton) {
      nextButton.disabled = isNextDisabled;
    }
    if (nextButtonWrapper) {
      nextButtonWrapper.setAttribute('aria-disabled', isNextDisabled);
      if (isNextDisabled) {
        nextButtonWrapper.style.pointerEvents = 'none';
      } else {
        nextButtonWrapper.style.pointerEvents = '';
      }
    }
  }

  // Find current active block index
  #findCurrentActiveIndex() {
    return this.refs.blocks.findIndex((block) => block.getAttribute('data-active') === 'true');
  }

  // Public API for external access
  goToBlock(index) {
    if (index < 0 || index >= this.#totalBlocks || index === this.#currentIndex) return;

    this.#currentIndex = index;
    this.#switchBlock();
  }

  getCurrentIndex() {
    return this.#currentIndex;
  }

  getTotalBlocks() {
    return this.#totalBlocks;
  }

  next() {
    this.#handleNext();
  }

  prev() {
    this.#handlePrev();
  }
}

// REQUIRED: Standard component initialization pattern
function initNMSSuperStarsActionSwitcher() {
  // Check for base class availability
  if (!window.NMSBaseClass) {
    console.error(
      'NMS Super Stars Action Switcher: NMSBaseClass not found. Make sure nms-base-class.js loads first.'
    );
    return;
  }

  const elements = document.querySelectorAll('nms-super-stars-action-switcher');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSSuperStarsActionSwitcher(element);
        instance.init();
        element.setAttribute('data-initialized', 'true');
      } catch (error) {
        console.error('Failed to initialize NMS Super Stars Action Switcher:', error, element);
      }
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSuperStarsActionSwitcher);
} else {
  initNMSSuperStarsActionSwitcher();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSSuperStarsActionSwitcher);
document.addEventListener('shopify:section:load', initNMSSuperStarsActionSwitcher);

// Export for debugging and manual initialization
window.initNMSSuperStarsActionSwitcher = initNMSSuperStarsActionSwitcher;

// Global API for accessing instances
window.getNMSSuperStarsActionSwitcher = function (sectionId) {
  if (sectionId) {
    return window.neemans?.superStarsAction?.[sectionId] || null;
  }
  return window.neemans?.superStarsAction || {};
};
